/* ============================================================================
   WIP Nav Tracking — fires a GA4 `nav_click` event for navigation link clicks
   ----------------------------------------------------------------------------
   Why this exists
     Maya Brenner's GA4 has never measured internal navigation clicks. Header
     nav numbers in the analytics reports were a proxy (page views of the
     destination, from every source). This sends the real click.

   Transport
     The site has NO working GTM container — layout/theme.liquid carries only
     the orphaned GTM noscript iframe, and we do not have access to that GTM
     account. GA4 arrives instead through Shopify's Web Pixels Manager: the
     Google & YouTube channel app pixel, configured with google_tag_ids
     ["AW-821570733", "G-WP434MZC1W"] and runtimeContext "OPEN". "OPEN" means
     that pixel runs in the page's own window, so `window.gtag` is available to
     theme code once consent is granted.

     So: prefer the gtag the Google channel already put on the page. If it
     never shows up, self-load gtag.js as a fallback with send_page_view:false
     so we never double-count page views.

   Consent
     Gated on Shopify's Customer Privacy API (Consentmo writes the visitor's
     choice into it). Uses snippets/consent-gate.liquid's WIPConsent helper
     when present, and falls back to querying the API directly when it is not
     — consent-gate.liquid exists on the `main` lineage but not on `dev`, and
     this file must behave identically on both.

   Debugging
     Add ?nav_debug=1 to any URL (or set window.NAV_TRACKING_DEBUG = true)
     to log every event and which transport carried it.

   Event: nav_click
     nav_item      link label clicked, e.g. "Necklaces"
     nav_parent    top-level menu the link sits under, "(top level)" if none
     nav_level     "top" | "child" | "grandchild"
     nav_location  "header_desktop" | "header_drawer" | "footer"
     nav_url       destination path, e.g. "/collections/necklaces"
   Each of the five is registered as an event-scoped custom dimension in GA4.

   Event: hero_product_view
     Fired on a view of one of the Custom Gold Necklace hero products, carrying
     the nav click that started the journey. GA4 cannot join two separate
     events within a session — nav_click and page_view sit side by side and no
     report can cross them — so the nav attribution has to be stamped onto the
     product view itself. That is the whole point of this event.

     hero_product  canonical label, e.g. "Custom Gold Necklace - 3 Letters"
     nav_item      |
     nav_parent    |  the last nav click this session, replayed out of
     nav_location  |  sessionStorage, or "(no nav click)" when the visitor
                   |  arrived some other way (search, ad, direct, a PDP link)
     nav_hops      page views between that nav click and this one. "0" means
                   the nav link landed straight here. "(none)" with no click.

     Reusing the nav_item / nav_parent / nav_location parameter NAMES is
     deliberate: GA4 event-scoped custom dimensions key off the parameter name,
     not the event, so the three already registered for nav_click report on
     this event with no extra registration. Only hero_product and nav_hops are
     new. And because both events carry nav_item, one exploration can put
     nav_click and hero_product_view side by side per nav item — that ratio is
     the efficiency of a nav item at delivering people to a hero product.

     hero_product is derived from the letter count rather than read off the
     product title on purpose: the two stores name these products differently
     ("Custom Gold Initial Necklace - 1 Letter or Charm" on US vs "Custom Gold
     Necklace - 1 Letter" on UK; "7 Letters" vs "7 Letter"), and the US and UK
     GA4 properties should report the same product under the same name.
   ========================================================================= */
(function () {
  'use strict';

  var MEASUREMENT_ID = 'G-WP434MZC1W';
  var EVENT_NAME = 'nav_click';
  var HERO_EVENT_NAME = 'hero_product_view';
  var GTAG_WAIT_MS = 6000; // how long to wait for the Google channel's gtag
  var GTAG_POLL_MS = 250;

  // The Custom Gold Necklace family, matched on the handle so a new letter
  // count is picked up without a code change. Tolerates the
  // /collections/<x>/products/<handle> form and a locale prefix.
  //   US 1-letter is custom-gold-necklace-1-letter-1, UK is ...-1-letter,
  //   hence the optional trailing -N and the optional plural.
  // The anchoring $ is what keeps out Zepto's personalizer clones, which append
  // _pplr_<digits>_<digits> to the same handle and are reached from the cart
  // rather than from navigation.
  var HERO_PATH_RE = new RegExp(
    '(?:^|/)products/custom-gold-necklace-([0-9]+)-letters?(?:-[0-9]+)?/?$'
  );

  // Where the last nav click is parked for the next page to read.
  var NAV_CONTEXT_KEY = 'wip_nav_last';
  // Roughly GA4's own 30-minute session window. sessionStorage already dies
  // with the tab; this stops a tab left open overnight from crediting a nav
  // click made yesterday.
  var NAV_CONTEXT_MAX_AGE_MS = 30 * 60 * 1000;

  var DEBUG =
    (typeof window.NAV_TRACKING_DEBUG !== 'undefined' && window.NAV_TRACKING_DEBUG) ||
    /[?&]nav_debug=1/.test(window.location.search);

  function log() {
    if (!DEBUG) return;
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[nav-tracking]');
      console.log.apply(console, args);
    } catch (e) {}
  }

  /* -- queue -------------------------------------------------------------- */
  // Clicks can happen before consent resolves or before gtag lands. Hold them
  // rather than dropping them; navigation is same-tab so the queue rarely has
  // to survive long, but a queued event still fires on a dropdown open + click.
  var queue = [];
  var transport = null; // function(params) once we can actually send

  function flush() {
    if (!transport) return;
    while (queue.length) {
      var item = queue.shift();
      try {
        transport(item.name, item.params);
      } catch (e) {
        log('send failed', e);
      }
    }
  }

  function send(name, params) {
    queue.push({ name: name, params: params });
    log('queued', name, params);
    flush();
  }

  /* -- consent ------------------------------------------------------------ */
  function analyticsAllowed() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return false;
    try {
      return cp.analyticsProcessingAllowed();
    } catch (e) {
      return false;
    }
  }

  function whenAnalyticsAllowed(cb) {
    // Preferred: the theme's own consent gate (main lineage).
    if (window.WIPConsent && typeof window.WIPConsent.whenAllowed === 'function') {
      log('using WIPConsent gate');
      window.WIPConsent.whenAllowed('analytics', cb);
      return;
    }

    // Fallback: same logic, self-contained (dev lineage has no consent-gate).
    log('WIPConsent absent, self-gating on customerPrivacy');
    var fired = false;
    function attempt() {
      if (fired || !analyticsAllowed()) return;
      fired = true;
      cb();
    }

    document.addEventListener('visitorConsentCollected', attempt);

    if (window.Shopify && window.Shopify.customerPrivacy) {
      attempt();
    } else if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (err) {
          if (!err) attempt();
        }
      );
    } else {
      // window.Shopify not ready yet — poll briefly (~10s) then give up.
      var tries = 0;
      var timer = setInterval(function () {
        tries++;
        if (window.Shopify && window.Shopify.customerPrivacy) {
          clearInterval(timer);
          attempt();
        } else if (tries > 100) {
          clearInterval(timer);
        }
      }, 100);
    }
  }

  /* -- transport ---------------------------------------------------------- */
  function useExistingGtag() {
    transport = function (name, params) {
      window.gtag('event', name, params);
    };
    log('transport: existing gtag (Google & YouTube channel)');
    flush();
  }

  function loadOwnGtag() {
    // Nothing put gtag on the page. Load our own, configured NOT to send a
    // page_view, so this only ever adds nav_click and never inflates traffic.
    var s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + MEASUREMENT_ID;
    s.onerror = function () {
      log('gtag.js failed to load — nav_click cannot be sent');
    };
    s.onload = function () {
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') {
        window.gtag = function () {
          window.dataLayer.push(arguments);
        };
      }
      window.gtag('js', new Date());
      window.gtag('config', MEASUREMENT_ID, { send_page_view: false });
      transport = function (name, params) {
        window.gtag('event', name, params);
      };
      log('transport: self-loaded gtag (fallback)');
      flush();
    };
    document.head.appendChild(s);
  }

  function initTransport() {
    if (typeof window.gtag === 'function') {
      useExistingGtag();
      return;
    }
    // Give the Google channel pixel a chance to land before we load our own.
    var waited = 0;
    var timer = setInterval(function () {
      if (typeof window.gtag === 'function') {
        clearInterval(timer);
        useExistingGtag();
        return;
      }
      waited += GTAG_POLL_MS;
      if (waited >= GTAG_WAIT_MS) {
        clearInterval(timer);
        log('no gtag after ' + GTAG_WAIT_MS + 'ms, falling back');
        loadOwnGtag();
      }
    }, GTAG_POLL_MS);
  }

  /* -- nav context (carries a nav click forward to later page views) ------ */
  // sessionStorage, not a cookie: it is per-tab, dies with the tab, and never
  // leaves the browser. It is written only inside the consent gate, so a
  // visitor who has not granted analytics consent gets nothing stored at all.
  function storage() {
    try {
      var s = window.sessionStorage;
      s.getItem(NAV_CONTEXT_KEY); // Safari private mode throws on access
      return s;
    } catch (e) {
      return null;
    }
  }

  function readNavContext() {
    var store = storage();
    if (!store) return null;
    try {
      var raw = store.getItem(NAV_CONTEXT_KEY);
      if (!raw) return null;
      var ctx = JSON.parse(raw);
      if (!ctx || typeof ctx.t !== 'number') return null;
      if (Date.now() - ctx.t > NAV_CONTEXT_MAX_AGE_MS) {
        store.removeItem(NAV_CONTEXT_KEY);
        return null;
      }
      return ctx;
    } catch (e) {
      return null;
    }
  }

  // The click listener is bound unconditionally (it has to be, to catch a click
  // that happens while consent is still resolving), so this is the gate for the
  // storage write itself. Without analytics consent nothing is ever stored.
  var consentGranted = false;

  function writeNavContext(ctx) {
    if (!consentGranted) return;
    var store = storage();
    if (!store) return;
    try {
      store.setItem(NAV_CONTEXT_KEY, JSON.stringify(ctx));
    } catch (e) {}
  }

  // Read once per page load, then bump the hop count for the NEXT page. So the
  // page the nav link landed on reads 0, the page after it reads 1, and so on.
  // A fresh click resets to 0, because the click happens after this has run.
  var navContext = null;

  function initNavContext() {
    navContext = readNavContext();
    if (!navContext) return;
    var next = {};
    for (var k in navContext) {
      if (Object.prototype.hasOwnProperty.call(navContext, k)) next[k] = navContext[k];
    }
    next.h = (typeof navContext.h === 'number' ? navContext.h : 0) + 1;
    writeNavContext(next);
    log('nav context', navContext);
  }

  /* -- hero product view -------------------------------------------------- */
  function heroLetters() {
    var match = HERO_PATH_RE.exec(String(window.location.pathname || '').toLowerCase());
    return match ? match[1] : null;
  }

  function heroLabel(letters) {
    // Canonical, store-independent. See the note at the top of this file.
    if (letters === '1') return 'Custom Gold Initial Necklace';
    return 'Custom Gold Necklace - ' + letters + ' Letters';
  }

  function hopBucket(hops) {
    // Bounded so the dimension stays low-cardinality; the interesting values
    // are the small ones anyway ("0" is the nav item that points straight here).
    return hops >= 10 ? '10+' : String(hops);
  }

  function fireHeroView() {
    var letters = heroLetters();
    if (!letters) return;

    var params = {
      hero_product: truncate(heroLabel(letters)),
      nav_item: truncate(navContext ? navContext.i : '(no nav click)'),
      nav_parent: truncate(navContext ? navContext.p : '(no nav click)'),
      nav_location: truncate(navContext ? navContext.l : '(no nav click)'),
      nav_hops: navContext ? hopBucket(navContext.h || 0) : '(none)'
    };

    send(HERO_EVENT_NAME, params);
  }

  /* -- click capture ------------------------------------------------------ */
  function text(el) {
    if (!el) return '';
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function truncate(value) {
    // GA4 caps event parameter values at 100 characters.
    value = String(value == null ? '' : value);
    return value.length > 100 ? value.slice(0, 100) : value;
  }

  function pathOf(link) {
    try {
      var url = new URL(link.href, window.location.href);
      return url.pathname + url.search;
    } catch (e) {
      return link.getAttribute('href') || '';
    }
  }

  // Derive the top-level menu a link belongs to, for links the Liquid markup
  // could not label directly (footer links have no parent menu item).
  function parentLabel(link) {
    var explicit = link.getAttribute('data-nav-parent');
    if (explicit) return explicit;

    var desktopItem = link.closest('.menu__item');
    if (desktopItem) {
      var topLink = desktopItem.querySelector('[data-top-link] .navtext');
      if (topLink) return text(topLink);
    }

    var panel = link.closest('.sliderule__panel');
    if (panel) {
      // The panel's own back row repeats the parent link. When that repeat is
      // what was clicked, its parent is one panel further up, not itself.
      if (link.closest('.sliderow--back')) {
        var outer = panel.parentElement && panel.parentElement.closest('.sliderule__panel');
        var outerBack = outer && outer.querySelector('.sliderow--back .sliderow__title');
        return outerBack ? text(outerBack) : '(top level)';
      }
      var back = panel.querySelector('.sliderow--back .sliderow__title');
      if (back) return text(back);
    }

    var footerBlock = link.closest('.footer__block--menu');
    if (footerBlock) {
      var heading = footerBlock.querySelector('.footer__block__title');
      if (heading) return text(heading);
    }

    return '(top level)';
  }

  function locationOf(link) {
    var explicit = link.getAttribute('data-nav-location');
    if (explicit) return explicit;
    if (link.closest('.footer__quicklinks')) return 'footer';
    if (link.closest('.drawer--header')) return 'header_drawer';
    if (link.closest('.header__menu')) return 'header_desktop';
    return '';
  }

  function handleClick(event) {
    var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!link) return;

    // Top-level items that only open a dropdown are marked href="#" in the
    // menu. Clicking one navigates nowhere, so it is not a nav click.
    var href = (link.getAttribute('href') || '').trim();
    if (href === '' || href === '#') return;

    var level = link.getAttribute('data-nav-level');
    var loc = locationOf(link);

    // Only track links we can positively place in a navigation region.
    if (!loc) return;
    if (!level) level = loc === 'footer' ? 'child' : 'top';

    var params = {
      nav_item: truncate(link.getAttribute('data-nav-item') || text(link)),
      nav_parent: truncate(parentLabel(link)),
      nav_level: truncate(level),
      nav_location: truncate(loc),
      nav_url: truncate(pathOf(link))
    };

    send(EVENT_NAME, params);

    // Hand this click to the next page so a later hero product view can say
    // which nav item started the journey.
    writeNavContext({
      i: params.nav_item,
      p: params.nav_parent,
      l: params.nav_location,
      u: params.nav_url,
      t: Date.now(),
      h: 0
    });
  }

  document.addEventListener('click', handleClick);
  // Middle-click / "open in new tab" is still a navigation choice worth counting.
  document.addEventListener('auxclick', function (event) {
    if (event.button === 1) handleClick(event);
  });

  /* -- go ----------------------------------------------------------------- */
  whenAnalyticsAllowed(function () {
    log('analytics consent granted');
    consentGranted = true;
    initTransport();
    initNavContext();
    // Queued if the transport is not up yet; flush() sends it the moment it is.
    fireHeroView();
  });
})();
